/**
 * ThemeCatalogDialog — CRUD over projects/{pid}/catalogs/theme_catalog.themes[].
 *
 * Sprint 14 refactor:
 *   • Embeds CompositeDataPicker (audiences / tasks / socialproofs / insights)
 *     inside the dialog — user picks WHICH entities feed the AI generator.
 *   • Generate via AI is now fire-and-forget: backend creates a backend_tasks
 *     doc with PhaseTracker, and persists themes directly into the catalog
 *     when done. Dialog reacts via useThemeCatalog's realtime listener +
 *     useBackendTask for the in-flight phase chip.
 *
 * Inline editing of id / name / description / defaultMixPct per row, plus
 * manual Add / Remove, still supported. Save overwrites the catalog with
 * the local draft once the user is done curating.
 */

import React, { useMemo, useState } from 'react';
import {
  Stack, Box, Typography, TextField, IconButton, Button, Alert, Chip,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { BaseDialog, DialogHeader, DialogContent, DialogFooter } from '@modules/ui-components';
import { CompositeDataPicker } from '@modules/data-pickers';
import { StrategyService } from '@modules/firebase-core/services/StrategyService';
import { useCompositeDataOrchestrator } from '@modules/strategy-blocks/hooks/useCompositeDataOrchestrator';
import { useBackendTask } from '@modules/content-agent';
import { useThemeCatalog } from '../hooks/useThemeCatalog';
import { ContentPlanApiClient } from '../services/ContentPlanApiClient';
import { ManualThemeDialog } from './theme/ManualThemeDialog';
import type { ContentTheme } from '../types';

const TESTIDS = {
  dialog: 'theme-catalog-dialog',
  row: 'theme-catalog-row',
  add: 'theme-catalog-add',
  generateAi: 'theme-catalog-generate-ai',
  save: 'theme-catalog-save',
  cancel: 'theme-catalog-cancel',
  pickers: 'theme-catalog-pickers',
  phaseChip: 'theme-catalog-phase-chip',
} as const;

export interface ThemeCatalogDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  userId: string;
  /** Strategy service — powers the CompositeDataPicker auto-loader. Same one used by GenerateTopicsDialog. */
  strategyService: StrategyService;
  /** Optional: name + description snippet for the AI prompt. */
  projectContext?: string;
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
}

export const ThemeCatalogDialog: React.FC<ThemeCatalogDialogProps> = ({
  open, onClose, projectId, userId, strategyService, projectContext,
}) => {
  const { themes: remoteThemes, loading, saveThemes } = useThemeCatalog(projectId);
  const [draft, setDraft] = useState<ContentTheme[] | null>(null);
  const [userHint, setUserHint] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentTaskId, setCurrentTaskId] = useState<string | null>(null);
  const [selectedPaths, setSelectedPaths] = useState<string[]>([]);
  // workflow-golden-tower — directive (no-AI) theme creation lives in a
  // separate dialog that routes through /agent/content-plan/themes/manual so
  // audienceFit[] + dataUsage validation is centralised on the backend.
  const [manualDialogOpen, setManualDialogOpen] = useState(false);

  // Composite data picker — identical stack to GenerateTopicsDialog / StrategyFormDialog.
  const orchestrator = useCompositeDataOrchestrator({
    projectId,
    strategyService,
    initialSelection: selectedPaths,
    selectAll: false,
    onSelectionChange: (d) => setSelectedPaths(d.selectedIds),
    open,
  });

  // Subscribe to the in-flight AI task so we can show phase progress +
  // clear the "Generating…" chip automatically once the task completes.
  const { task: aiTask } = useBackendTask(projectId, currentTaskId);
  const taskStatus = (aiTask as unknown as { status?: string } | null)?.status ?? null;
  const taskCurrentPhase = (aiTask as unknown as { currentPhaseKey?: string } | null)?.currentPhaseKey ?? null;
  const isGenerating = currentTaskId !== null && taskStatus !== 'completed' && taskStatus !== 'error';

  // When the task completes, remoteThemes updates via the listener — merge
  // new ones into our draft so inline edits aren't overwritten.
  React.useEffect(() => {
    if (taskStatus === 'completed' || taskStatus === 'error') {
      setCurrentTaskId(null);
      if (taskStatus === 'error') {
        const msg = (aiTask as unknown as { error?: string } | null)?.error ?? 'theme generation failed';
        setError(msg);
      }
    }
  }, [taskStatus, aiTask]);

  // Initialise draft from remote once the dialog opens.
  React.useEffect(() => {
    if (open && draft === null && !loading) {
      setDraft([...remoteThemes]);
    }
    if (!open) {
      // Reset on close so re-opening shows a fresh snapshot.
      setDraft(null);
      setCurrentTaskId(null);
      setError(null);
    }
  }, [open, loading, remoteThemes, draft]);

  // While the AI task is live, remoteThemes grows — surface the fresh ones
  // into the draft so the user sees them immediately without losing edits.
  React.useEffect(() => {
    if (!isGenerating && taskStatus !== 'completed') return;
    if (draft === null) return;
    const draftIds = new Set(draft.map((t) => t.id));
    const fresh = remoteThemes.filter((t) => !draftIds.has(t.id));
    if (fresh.length > 0) setDraft([...draft, ...fresh]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remoteThemes, isGenerating, taskStatus]);

  const current: ContentTheme[] = draft ?? remoteThemes;

  const pickerSelectionCount = useMemo(() => {
    const ids = orchestrator.aiSourceDataIds;
    if (!ids) return 0;
    return (
      (ids.audienceIds?.length ?? 0) +
      (ids.taskIds?.length ?? 0) +
      (ids.reviewIds?.length ?? 0) +
      (ids.studyCaseIds?.length ?? 0) +
      (ids.ratingIds?.length ?? 0) +
      (ids.bigNumberIds?.length ?? 0)
    );
  }, [orchestrator.aiSourceDataIds]);

  const updateRow = (idx: number, patch: Partial<ContentTheme>) => {
    if (draft === null) setDraft([...remoteThemes]);
    setDraft((prev) => (prev ?? remoteThemes).map((t, i) => i === idx ? { ...t, ...patch } as ContentTheme : t));
  };

  const removeRow = (idx: number) => {
    setDraft((prev) => (prev ?? remoteThemes).filter((_, i) => i !== idx));
  };

  const addRow = () => {
    setManualDialogOpen(true);
  };

  const handleGenerate = async () => {
    setError(null);
    try {
      const client = new ContentPlanApiClient();
      const response = await client.generateThemes({
        projectId, userId,
        sourceData: orchestrator.hydratedData,
        sourceDataIds: orchestrator.aiSourceDataIds as any,
        existingThemeIds: current.map((t) => t.id),
        count: 7,
        projectContext,
        userHint: userHint.trim() || undefined,
      });
      if (!response.success) throw new Error(response.error ?? 'theme generation failed');
      setCurrentTaskId(response.taskId);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const handleSave = async () => {
    setError(null); setSubmitting(true);
    try {
      // Normalise IDs — if name was edited, regenerate id when still the default-placeholder.
      const normalised = current.map((t) => {
        if (!t.id || t.id.startsWith('theme_')) {
          return { ...t, id: slugify(t.name || t.id) || t.id };
        }
        return t;
      });
      await saveThemes(normalised);
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <BaseDialog open={open} onClose={onClose} maxWidth="md" data-testid={TESTIDS.dialog}>
      <DialogHeader
        title="Theme Catalog"
        subtitle="Project-scoped vocabulary used by content-plan sprints + topics"
        onClose={onClose}
      />
      <DialogContent>
        <Stack spacing={2} sx={{ py: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          {/* Source data picker — chooses WHICH entities feed the AI generator. */}
          <Box data-testid={TESTIDS.pickers}>
            <Typography variant="subtitle2" gutterBottom>
              Source data for AI generation
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Pick audiences / tasks / insights you want the AI to anchor themes on.
              The prompt will cite these entity IDs per generated theme.
            </Typography>
            <CompositeDataPicker
              {...orchestrator.compositeProps}
              pickers={[
                { type: 'audience' as const, label: 'Audiences', enabled: true, selectAll: false },
                { type: 'task' as const, label: 'Tasks', enabled: true, selectAll: false },
                { type: 'socialproof' as const, label: 'Social Proof', enabled: true, selectAll: false },
                { type: 'insights' as const, label: 'Insights', enabled: true, selectAll: false },
              ]}
            />
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              label="AI hint (optional)"
              placeholder="e.g. focus on B2B SaaS, skip promo themes"
              value={userHint}
              onChange={(e) => setUserHint(e.target.value)}
              size="small"
              sx={{ flex: 1 }}
              disabled={isGenerating}
            />
            <Button
              variant="outlined"
              startIcon={<AutoFixHighIcon />}
              onClick={handleGenerate}
              disabled={isGenerating || pickerSelectionCount === 0}
              data-testid={TESTIDS.generateAi}
            >
              {isGenerating ? 'Generating…' : 'Generate via AI'}
            </Button>
            {isGenerating && (
              <Chip
                size="small"
                color="info"
                label={taskCurrentPhase ? `AI · ${taskCurrentPhase}` : 'AI · queued'}
                data-testid={TESTIDS.phaseChip}
              />
            )}
          </Stack>
          {pickerSelectionCount === 0 && (
            <Typography variant="caption" color="text.secondary">
              Select at least one audience / task / insight above to enable AI generation.
            </Typography>
          )}

          {current.length === 0 ? (
            <Alert severity="info">
              No themes yet. Click "Generate via AI" to seed 5-10 themes based on your picked source data,
              or "Add theme" to create manually.
            </Alert>
          ) : (
            <Stack spacing={1}>
              {current.map((t, idx) => (
                <Stack
                  key={`${t.id}_${idx}`}
                  direction="row"
                  spacing={1}
                  alignItems="flex-start"
                  sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1 }}
                  data-testid={TESTIDS.row}
                >
                  <Stack spacing={0.5} sx={{ flex: 1 }}>
                    <Stack direction="row" spacing={1}>
                      <TextField
                        label="id"
                        size="small"
                        value={t.id}
                        onChange={(e) => updateRow(idx, { id: slugify(e.target.value) })}
                        sx={{ width: 180 }}
                      />
                      <TextField
                        label="name"
                        size="small"
                        value={t.name ?? ''}
                        onChange={(e) => updateRow(idx, { name: e.target.value } as any)}
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        label="mix %"
                        size="small"
                        type="number"
                        value={t.defaultMixPct ?? 0}
                        onChange={(e) => updateRow(idx, { defaultMixPct: parseFloat(e.target.value) || 0 } as any)}
                        inputProps={{ step: 0.05, min: 0, max: 1 }}
                        sx={{ width: 100 }}
                      />
                    </Stack>
                    <TextField
                      label="description"
                      size="small"
                      multiline
                      value={(t as any).description ?? ''}
                      onChange={(e) => updateRow(idx, { description: e.target.value } as any)}
                    />
                  </Stack>
                  <IconButton onClick={() => removeRow(idx)} size="small" color="error">
                    <DeleteOutlineIcon />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
          )}

          <Stack direction="row" spacing={1} sx={{ alignSelf: 'flex-start' }}>
            <Button
              variant="text"
              startIcon={<AddIcon />}
              onClick={addRow}
              data-testid={TESTIDS.add}
            >
              Add manually
            </Button>
          </Stack>

          {current.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              Sum of mix % : {current.reduce((s, t) => s + (t.defaultMixPct ?? 0), 0).toFixed(2)} (should be ~1.0)
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogFooter
        secondaryAction={{
          label: 'Cancel', onClick: onClose, 'data-testid': TESTIDS.cancel,
        } as any}
        primaryAction={{
          label: submitting ? 'Saving…' : `Save (${current.length} themes)`,
          onClick: handleSave,
          disabled: submitting || isGenerating,
          'data-testid': TESTIDS.save,
        } as any}
      />

      {/* Validated manual-theme path — appends through the backend endpoint so
          the listener on useThemeCatalog picks up the new theme automatically. */}
      <ManualThemeDialog
        open={manualDialogOpen}
        onClose={() => setManualDialogOpen(false)}
        projectId={projectId}
        userId={userId}
      />
    </BaseDialog>
  );
};

export default ThemeCatalogDialog;
